<!--
@component
MatchCardMatchSked

See markup below.
-->

<script lang="ts">
  import type { Match } from '$lib/@types/opta';
  import { appState } from '$lib/stores/index.svelte';
  import { onMount } from 'svelte';
  import { getAwayTeam, getHomeTeam } from '$utils/getMatchStats';
  import Team from './Team.svelte';

  type Props = {
    match: Match;
    flagLink?: boolean;
  };

  let { match, flagLink = false }: Props = $props();

  const homeTeam = $derived(getHomeTeam(match));
  const awayTeam = $derived(getAwayTeam(match));
  // Pre-formatted `{ month, day, time }` decorated onto each match by
  // `getData()` (see `$utils/data/endpoints.js`) — no per-render date math.
  const matchDate = $derived(match.localDateStamp);
  const isGroupStage = $derived(match.matchInfo?.stage?.name === 'Group Stage');

  // The local date stamp is computed in the viewer's timezone, so SSR
  // and client renders can disagree. Show `…` until the component has
  // mounted on the client to avoid a visible flash/shift.
  let mounted = $state(false);
  onMount(() => {
    mounted = true;
  });

  let cardWidth = $state(0);
  let cardHeight = $state(0);
</script>

<div
  class="match-card-wrapper isFixture"
  class:group={isGroupStage}
  bind:clientWidth={cardWidth}
  bind:clientHeight={cardHeight}
  style="--cardWidth: {cardWidth}px; --cardHeight: {cardHeight}px"
>
  <div class="results-wrapper">
    <div class="date-time">
      <div class="date">
        {#if !mounted || appState.isLoading || !matchDate}
          <!-- Don't show date until the local time format has loaded -->
          &hellip;
        {:else}
          <span class="day">{matchDate.month} {matchDate.day}</span>
          <span class="time">{matchDate.time}</span>
        {/if}
      </div>
    </div>

    <Team team={homeTeam} side="home" {flagLink} fallback="TBD" />
    <Team team={awayTeam} side="away" {flagLink} fallback="TBD" />
  </div>
</div>

<style lang="scss">
  @use '../../../styles/mixins' as mixins;
  @use 'layout' as *;

  .match-card-wrapper.isFixture {
    .results-wrapper {
      border-radius: var(--theme-style-border-radius);
    }
  }

  .date-time {
    top: unset;
    border-radius: calc(0.5 * var(--theme-style-border-radius));

    .date {
      width: auto;

      .day {
        margin-bottom: 0;
      }
    }

    .time {
      @include mixins.font-regular;
      @include mixins.text-secondary;
      @include mixins.text-sm;
      text-align: center;
      margin-bottom: 0;
    }
  }
</style>
